import type { AggregatorSmsTypeMap, CallPrices, SmsTypeBillingMap } from './types';
import type { BuyPriceEntry } from './normalizers';

const PRICE_PRECISION = 4;

export function roundConvertedPrice(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** PRICE_PRECISION;
  return Math.round(value * factor) / factor;
}

function convertAggregatorSmsTypeMap(
  map: AggregatorSmsTypeMap,
  rate: number,
): AggregatorSmsTypeMap {
  const out: AggregatorSmsTypeMap = {};
  for (const [aggKey, typeMap] of Object.entries(map)) {
    const convertedTypes: AggregatorSmsTypeMap[string] = {};
    for (const [typeKey, billing] of Object.entries(typeMap ?? {})) {
      convertedTypes[typeKey] = {
        steps: billing.steps?.map((s) => ({
          ...s,
          price: roundConvertedPrice(s.price * rate),
        })),
        packages: billing.packages?.map((p) => ({
          ...p,
          price: roundConvertedPrice(p.price * rate),
        })),
      };
    }
    out[aggKey] = convertedTypes;
  }
  return out;
}

function convertSmsTypeBillingMap(map: SmsTypeBillingMap, rate: number): SmsTypeBillingMap {
  const out: SmsTypeBillingMap = {};
  for (const [typeKey, billing] of Object.entries(map ?? {})) {
    out[typeKey] = {
      steps: billing.steps?.map((s) => ({
        ...s,
        price: roundConvertedPrice(s.price * rate),
      })),
      packages: billing.packages?.map((p) => ({
        ...p,
        price: roundConvertedPrice(p.price * rate),
      })),
    };
  }
  return out;
}

export interface SmsPriceUiShape {
  country_iso: string;
  operator_id: number | null;
  direction_id: string;
  sell_prices: SmsTypeBillingMap;
  buy_prices: AggregatorSmsTypeMap;
}

function plainCloneAggregatorSmsTypeMap(map: AggregatorSmsTypeMap): AggregatorSmsTypeMap {
  const out: AggregatorSmsTypeMap = {};
  for (const [aggKey, typeMap] of Object.entries(map ?? {})) {
    out[aggKey] = {};
    for (const [typeKey, billing] of Object.entries(typeMap ?? {})) {
      out[aggKey][typeKey] = {
        steps: billing.steps?.map((s) => ({
          from: s.from,
          to: s.to ?? null,
          price: s.price,
        })),
        packages: billing.packages?.map((p) => ({
          from: p.from,
          to: p.to,
          price: p.price,
        })),
      };
    }
  }
  return out;
}

function plainCloneSmsTypeBillingMap(map: SmsTypeBillingMap): SmsTypeBillingMap {
  const out: SmsTypeBillingMap = {};
  for (const [typeKey, billing] of Object.entries(map ?? {})) {
    out[typeKey] = {
      steps: billing.steps?.map((s) => ({
        from: s.from,
        to: s.to ?? null,
        price: s.price,
      })),
      packages: billing.packages?.map((p) => ({
        from: p.from,
        to: p.to,
        price: p.price,
      })),
    };
  }
  return out;
}

function plainCloneSmsPriceUiEntry<T extends SmsPriceUiShape>(entry: T): T {
  return {
    country_iso: entry.country_iso,
    operator_id: entry.operator_id,
    direction_id: entry.direction_id,
    sell_prices: plainCloneSmsTypeBillingMap(entry.sell_prices),
    buy_prices: plainCloneAggregatorSmsTypeMap(entry.buy_prices),
  } as T;
}

export function convertSmsPriceUiEntries<T extends SmsPriceUiShape>(
  entries: T[],
  rate: number,
): T[] {
  return entries.map((entry) => {
    const base = plainCloneSmsPriceUiEntry(entry);
    return {
      ...base,
      sell_prices: convertSmsTypeBillingMap(base.sell_prices, rate),
      buy_prices: convertAggregatorSmsTypeMap(base.buy_prices, rate),
    };
  });
}

export interface CallPriceUiShape {
  country_iso: string;
  operator_id: number | null;
  direction_id: string;
  prices: CallPrices;
  buyPriceEntries: BuyPriceEntry<CallPrices>[];
}

function convertCallPrices(prices: CallPrices, rate: number): CallPrices {
  return {
    phone_rent: roundConvertedPrice((prices.phone_rent ?? 0) * rate),
    income_calls: roundConvertedPrice((prices.income_calls ?? 0) * rate),
    outcome_calls: roundConvertedPrice((prices.outcome_calls ?? 0) * rate),
  };
}

function plainCloneCallPriceUiEntry<T extends CallPriceUiShape>(entry: T): T {
  return {
    country_iso: entry.country_iso,
    operator_id: entry.operator_id,
    direction_id: entry.direction_id,
    prices: {
      phone_rent: entry.prices.phone_rent ?? 0,
      income_calls: entry.prices.income_calls ?? 0,
      outcome_calls: entry.prices.outcome_calls ?? 0,
    },
    buyPriceEntries: entry.buyPriceEntries.map((e) => ({
      aggregator_id: e.aggregator_id,
      payload: {
        phone_rent: e.payload.phone_rent ?? 0,
        income_calls: e.payload.income_calls ?? 0,
        outcome_calls: e.payload.outcome_calls ?? 0,
      },
    })),
  } as T;
}

export function convertCallPriceUiEntries<T extends CallPriceUiShape>(
  entries: T[],
  rate: number,
): T[] {
  return entries.map((entry) => {
    const base = plainCloneCallPriceUiEntry(entry);
    return {
      ...base,
      prices: convertCallPrices(base.prices, rate),
      buyPriceEntries: base.buyPriceEntries.map((e) => ({
        ...e,
        payload: convertCallPrices(e.payload, rate),
      })),
    };
  });
}
